import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion, useInView, animate, AnimatePresence } from "framer-motion";

import Fondo1 from "../static/media/Fondo3.webp";

const MotionBox = motion(Box);

// --- Componente de Contador Animado ---
function AnimatedCounter({
  value,
  prefix = "",
  suffix = "",
  inView,
  delay = 0,
}) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (inView) {
      const controls = animate(0, value, {
        duration: 1.5,
        delay: delay,
        ease: "easeOut",
        onUpdate(val) {
          setCount(Math.floor(val));
        },
      });
      return controls.stop;
    }
  }, [value, inView, delay]);

  return (
    <>
      {prefix}
      {count}
      {suffix}
    </>
  );
}

const benefits = [
  {
    tag: "GESTIÓN",
    title: "Gestión\nunificada",
    tagline: "Todo en un solo sistema",
    description:
      "Ventas, inventario y facturación comparten la misma información en tiempo real. Sin dobles registros, sin desincronización, sin hojas de cálculo paralelas.",
    accent: "#2C5FBE",
    geo: (
      <svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        style={{ position: "absolute", top: 16, right: 16, opacity: 0.12 }}
      >
        <polygon points="32,4 60,20 60,44 32,60 4,44 4,20" fill="#2C5FBE" />
      </svg>
    ),
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <rect x="3" y="3" width="11" height="11" rx="3" fill="#2C5FBE" />
        <rect
          x="18"
          y="3"
          width="11"
          height="11"
          rx="3"
          fill="#2C5FBE"
          opacity="0.4"
        />
        <rect
          x="3"
          y="18"
          width="11"
          height="11"
          rx="3"
          fill="#2C5FBE"
          opacity="0.4"
        />
        <rect
          x="18"
          y="18"
          width="11"
          height="11"
          rx="3"
          fill="#2C5FBE"
          opacity="0.7"
        />
        <path d="M8.5 8.5h15v15" stroke="white" strokeWidth="0" />
      </svg>
    ),
    metricValue: 4,
    metricPrefix: "",
    metricSuffix: " módulos",
    metricLabel: "integrados",
  },
  {
    tag: "EFICIENCIA",
    title: "Flujo\neficiente",
    tagline: "Menos pasos, más operación",
    description:
      "Cada módulo está pensado para reducir los clics necesarios. Emite una factura, registra una compra o revisa tu inventario sin navegar entre pantallas complejas.",
    accent: "#1A3D8F",
    geo: (
      <svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        style={{ position: "absolute", top: 16, right: 16, opacity: 0.1 }}
      >
        <polygon points="32,4 60,32 32,60 4,32" fill="#1A3D8F" />
      </svg>
    ),
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path
          d="M4 16h24M20 8l8 8-8 8"
          stroke="#2C5FBE"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="8" cy="16" r="2.5" fill="#2C5FBE" opacity="0.4" />
      </svg>
    ),
    metricValue: 70,
    metricPrefix: "-",
    metricSuffix: "%",
    metricLabel: "menos clics",
  },
  {
    tag: "CONFIANZA",
    title: "Imagen de\nconfianza",
    tagline: "Una plataforma que proyecta solidez",
    description:
      "Documentos electrónicos impecables, interfaz consistente y datos siempre ordenados. Cada interacción con tu cliente transmite profesionalismo.",
    accent: "#4F8CFF",
    geo: (
      <svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        style={{ position: "absolute", top: 16, right: 16, opacity: 0.1 }}
      >
        <polygon points="32,4 58,48 6,48" fill="#4F8CFF" />
      </svg>
    ),
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
        <path
          d="M16 3l10 4v8c0 6-4.5 11-10 13C10.5 26 6 21 6 15V7l10-4z"
          stroke="#2C5FBE"
          strokeWidth="2"
          fill="none"
        />
        <path
          d="M11 16l3 3 7-7"
          stroke="#2C5FBE"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    metricValue: 100,
    metricPrefix: "",
    metricSuffix: "%",
    metricLabel: "documentos válidos",
  },
];

function BenefitCard({ benefit, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <MotionBox
      ref={ref}
      // Animación de entrada más pronunciada (escala + movimiento)
      initial={{ opacity: 0, y: 50, scale: 0.95 }}
      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{
        duration: 0.6,
        delay: index * 0.15,
        ease: [0.25, 0.1, 0.25, 1],
      }} // Curva suave
      // Hover más dinámico con efecto resorte (spring)
      whileHover={{
        y: -10,
        scale: 1.02,
        transition: { type: "spring", stiffness: 300 },
      }}
      sx={{
        position: "relative",
        bgcolor: "#fff",
        borderRadius: "24px",
        p: { xs: 3.5, md: 4 },
        overflow: "hidden",
        border: "1px solid rgba(44,95,190,0.08)",
        boxShadow: "0 8px 32px rgba(15,23,42,0.06)",
        cursor: "default",
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,

        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px", // Ligeramente más grueso
          background: `linear-gradient(90deg, ${benefit.accent} 0%, #4F8CFF 100%)`,
          borderRadius: "24px 24px 0 0",
        },

        "&:hover .card-glow": {
          opacity: 1,
        },
      }}
    >
      {/* Fondo geométrico */}
      {benefit.geo}

      {/* Glow hover: Más pronunciado */}
      <Box
        className="card-glow"
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 80% 20%, rgba(44,95,190,0.06) 0%, transparent 60%)",
          opacity: 0,
          transition: "opacity 0.4s ease",
          pointerEvents: "none",
        }}
      />

      {/* Tag */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            bgcolor: "rgba(44,95,190,0.08)",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {benefit.icon}
        </Box>
        <Typography
          sx={{
            fontSize: "0.68rem",
            fontWeight: 800,
            letterSpacing: "0.14em",
            color: "#2C5FBE",
            textTransform: "uppercase",
          }}
        >
          {benefit.tag}
        </Typography>
      </Box>

      {/* Título */}
      <Typography
        component="h3"
        sx={{
          fontWeight: 800,
          fontSize: { xs: "1.7rem", md: "2rem" },
          color: "#1A2B4A",
          lineHeight: 1.05,
          whiteSpace: "pre-line",
          mt: 1,
        }}
      >
        {benefit.title}
      </Typography>

      {/* Tagline */}
      <Typography
        sx={{
          fontSize: "0.82rem",
          fontWeight: 700,
          color: "#2C5FBE",
          letterSpacing: "0.02em",
        }}
      >
        {benefit.tagline}
      </Typography>

      {/* Divider */}
      <Box
        sx={{
          width: 40,
          height: 2,
          background: "linear-gradient(90deg, #2C5FBE 0%, transparent 100%)",
          borderRadius: 1,
          my: 1,
        }}
      />

      {/* Descripción */}
      <Typography
        sx={{
          color: "#475569",
          fontSize: "0.92rem",
          lineHeight: 1.65,
          flex: 1,
        }}
      >
        {benefit.description}
      </Typography>

      {/* Métrica Animada */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 0.5,
          mt: "auto",
          pt: 2.5,
          borderTop: "1px solid rgba(44,95,190,0.06)",
        }}
      >
        <Typography
          sx={{
            fontSize: "2.2rem",
            fontWeight: 800,
            color: "#2C5FBE",
            lineHeight: 1,
            letterSpacing: "-0.03em",
          }}
        >
          <AnimatedCounter
            value={benefit.metricValue}
            prefix={benefit.metricPrefix}
            suffix={benefit.metricSuffix}
            inView={inView}
            delay={index * 0.15 + 0.3} // El contador empieza justo después de que entra la tarjeta
          />
        </Typography>
        <Typography
          sx={{
            fontSize: "0.78rem",
            color: "#94A3B8",
            fontWeight: 600,
            mb: 0.4,
          }}
        >
          {benefit.metricLabel}
        </Typography>
      </Box>
    </MotionBox>
  );
}

export default function BenefitsSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeIndex, setActiveIndex] = useState(0);

  // Auto-play del carrusel en móvil (cambia cada 5 segundos)
  useEffect(() => {
    if (!isMobile) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev >= benefits.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [isMobile]);

  // Manejo de swipe
  const handleDragEnd = (e, { offset }) => {
    const swipe = offset.x;
    if (swipe < -40) {
      setActiveIndex((prev) => (prev >= benefits.length - 1 ? 0 : prev + 1));
    } else if (swipe > 40) {
      setActiveIndex((prev) => (prev === 0 ? benefits.length - 1 : prev - 1));
    }
  };
  return (
    <Box
      id="features"
      sx={{
        py: { xs: 8, md: 10, lg: 12 }, // Ajustado para mejor distribución de pantalla
        position: "relative",
        overflow: "hidden",
        bgcolor: "#F8FAFF",
        backgroundImage: `url(${Fondo1})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Decoración geométrica de fondo */}
      <Box
        sx={{
          position: "absolute",
          top: -80,
          right: -80,
          width: 400,
          height: 400,
          background:
            "radial-gradient(circle, rgba(44,95,190,0.05) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -60,
          left: -60,
          width: 300,
          height: 300,
          borderRadius: "50%",
          border: "1px solid rgba(44,95,190,0.06)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg">
        {/* Header - Sin la línea superior */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{
            mb: { xs: 6, md: 8 },
            maxWidth: 600,
            mx: "auto",
            textAlign: { xs: "left", md: "center" },
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "2.2rem", md: "3rem" },
              color: "#1A2B4A",
              lineHeight: 1.1,
              mb: 2,
            }}
          >
            Factura, vende y crece{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #2C5FBE 0%, #4F8CFF 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              sin complicarte
            </Box>
          </Typography>

          <Typography
            sx={{
              color: "#475569",
              fontSize: { xs: "1rem", md: "1.1rem" },
              lineHeight: 1.6,
            }}
          >
            Diseñado para que tu negocio opere sin fricciones.
          </Typography>
        </MotionBox>

        {/* Cards: Slider en Móvil / Grid en Desktop */}
        {isMobile ? (
          <Box sx={{ position: "relative", overflow: "visible", px: 1, pb: 2 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={handleDragEnd}
                style={{ touchAction: "pan-y", cursor: "grab" }}
              >
                <BenefitCard benefit={benefits[activeIndex]} index={0} />
              </motion.div>
            </AnimatePresence>

            {/* Dots indicadores */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                gap: 1.5,
              }}
            >
              {benefits.map((b, i) => (
                <Box
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  sx={{
                    width: i === activeIndex ? 28 : 8,
                    height: 8,
                    borderRadius: 4,
                    bgcolor:
                      i === activeIndex ? b.accent : "rgba(44,95,190,0.15)",
                    cursor: "pointer",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      bgcolor:
                        i === activeIndex ? b.accent : "rgba(44,95,190,0.3)",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: { xs: 3, lg: 4 },
              alignItems: "stretch",
            }}
          >
            {benefits.map((benefit, i) => (
              <BenefitCard key={i} benefit={benefit} index={i} />
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
}
